<script lang="ts" setup>
import { ref, computed, watch } from 'vue';
import { cloneDeep } from 'lodash';

import { i18n } from '@apps/main/modules/localization/localizationSetup';
import { useBrowserStore } from '@apps/main/new-desktop/core/stores';

import type { Location } from '@libs/maps';
import type { AppRadioButtonOption } from '@libs/ui';
import {
  AppButton,
  AppCircularRadioButtons,
  AppMessageBox,
  AppSvgIcon,
} from '@libs/ui';
import type {
  FlightAutoCompleteSuggestion,
  PortInfo,
} from '@libs/trip-planner-interfaces';

import { AutoCompleteType, AutoCompleteSuggestions } from '../autoComplete';

import { useAddFlighstModalStore } from './store/addFlightsModalStore';
import type {
  AddFlightsModalInitialRoute,
  AddFligthsModalRoute,
} from './addFlightsModal.model';

const { t } = i18n.global;

const browserStore = useBrowserStore();
const addFlightsModalStore = useAddFlighstModalStore();

interface AddFlightsBannerProps {
  routes: AddFlightsModalInitialRoute[];
  startPositon: Location | undefined;
  endPosition: Location | undefined;
}
const props = defineProps<AddFlightsBannerProps>();

interface AddFlightsBannerEmits {
  (event: 'add-flights', value: AddFligthsModalRoute[]): void;
}
const emit = defineEmits<AddFlightsBannerEmits>();

const OPTIONS: AppRadioButtonOption[] = [
  {
    id: '1',
    label: t('planner.round_trip'),
    active: 1,
    ariaLabel: t('planner.round_trip'),
  },
  {
    id: '2',
    label: t('planner.one_way_flight'),
    active: 2,
    ariaLabel: t('planner.one_way_flight'),
  },
];
const shouldAddReturnFlight = computed<boolean>(
  () => selectedOption.value === '1'
);
const selectedOption = ref<string>('1');
const addFlightButtonText = computed<string>(
  () =>
    `+ ${
      shouldAddReturnFlight.value
        ? t('modals.addFlights.buttons.addFlights')
        : t('planner.transport.add_flight')
    }`
);

const isRouteDisabled = computed<boolean>(
  () =>
    !!addFlightsModalStore.routes[0] &&
    !!addFlightsModalStore.routes[0].fromAirport.code &&
    !!addFlightsModalStore.routes[0].toAirport.code &&
    addFlightsModalStore.routes[0].fromAirport.code ===
      addFlightsModalStore.routes[0].toAirport.code
);

//NOTE: duplicated from AddFlightViewNew. On finish experiment, if we decide to go with this, extract into a shared component
watch(
  () => props.routes,
  () => {
    addFlightsModalStore.initializeRoutes(
      props.routes,
      props.startPositon?.position,
      props.endPosition?.position
    );
  },
  { immediate: true }
);

function updateAirport(airportSuggestion: FlightAutoCompleteSuggestion): void {
  addFlightsModalStore.updateAirport(airportSuggestion);
}

function openAirportSelection(
  airport: PortInfo,
  isEditingFromAirport: boolean
): void {
  addFlightsModalStore.setEditingAirport(airport, 0, isEditingFromAirport);
}

function closeAirportSelection(): void {
  addFlightsModalStore.clearEditingAirport();
}

function handleConfirm(): void {
  if (addFlightsModalStore.isEditing) {
    closeAirportSelection();
    return;
  }
  if (shouldAddReturnFlight.value) {
    const routes: AddFligthsModalRoute[] = [];
    const departureFlight = cloneDeep(addFlightsModalStore.routes[0]);
    const returnFlight = cloneDeep(addFlightsModalStore.routes[1]);
    if (departureFlight && returnFlight) {
      routes.push({ ...departureFlight, isEnabled: true });
      routes.push({
        ...returnFlight,
        isEnabled: true,
        toAirport: departureFlight.fromAirport,
        fromAirport: departureFlight.toAirport,
      });
    }
    emit('add-flights', routes);
  } else if (addFlightsModalStore.routes[0]) {
    const departureFlight = cloneDeep(addFlightsModalStore.routes[0]);
    emit('add-flights', [{ ...departureFlight, isEnabled: true }]);
  }
}
</script>

<template>
  <div class="add-flights-banner">
    <div class="return-one-way-wrapper">
      <AppCircularRadioButtons
        :items="OPTIONS"
        :selected-item="selectedOption"
        :is-small-device="browserStore.isSmallerThan900px"
        orientation="horizontal"
        @toggle-selected="selectedOption = $event"
      />
    </div>
    <AppMessageBox
      v-if="isRouteDisabled"
      :text="$t('modals.addFlights.different_airports_validation')"
      message-type="error-light"
      :with-border="true"
      class="message-box"
    >
      <template #left-icon>
        <AppSvgIcon name="infoCircle" color="color-neutral-70" />
      </template>
    </AppMessageBox>
    <div
      v-if="addFlightsModalStore.routes[0]"
      class="airports-selector-wrapper"
    >
      <!-- NOTE: duplicated from AddFlightViewNew. On finish experiment, if we decide to go with this, extract into a shared component -->
      <AutoCompleteSuggestions
        :auto-complete-type="AutoCompleteType.Airport"
        :initial-search-text="
          addFlightsModalStore.routes[0].fromAirport.label ?? ''
        "
        :placeholder="$t('modals.addFlights.choose_departing_airport')"
        :initial-user-position="startPositon?.position"
        use-teleport
        @on-input-click="
          openAirportSelection(addFlightsModalStore.routes[0].fromAirport, true)
        "
        @airport-selected="updateAirport($event)"
        @close="closeAirportSelection"
      />
      <AutoCompleteSuggestions
        :auto-complete-type="AutoCompleteType.Airport"
        :initial-search-text="
          addFlightsModalStore.routes[0].toAirport.label ?? ''
        "
        :placeholder="$t('modals.addFlights.choose_arriving_airport')"
        :initial-user-position="startPositon?.position"
        use-teleport
        @on-input-click="
          openAirportSelection(addFlightsModalStore.routes[0].toAirport, false)
        "
        @airport-selected="updateAirport($event)"
        @close="closeAirportSelection"
      />
    </div>
    <AppButton
      type="primary"
      :state="isRouteDisabled ? 'disabled' : 'enabled'"
      :size="browserStore.isSmallerThan900px ? 'medium' : 'large'"
      :wide="true"
      @click="handleConfirm"
    >
      {{ addFlightButtonText }}
    </AppButton>
  </div>
</template>

<style lang="scss" scoped>
.add-flights-banner {
  display: grid;
  grid-template-rows: auto auto auto;
  grid-template-columns: 1fr min-content;
  gap: var(--spacing-4) var(--spacing-8);
  @include for-less-or-equal-than-900-px {
    grid-template-rows: auto auto auto;
    grid-template-columns: auto;
    gap: var(--spacing-4);
    .airports-selector-wrapper {
      margin-bottom: var(--spacing-12);
    }
  }
  .return-one-way-wrapper,
  .message-box {
    grid-column: 1/-1;
  }
  .airports-selector-wrapper {
    display: flex;
    flex-direction: row;
    gap: var(--spacing-4);
    @include for-less-or-equal-than-900-px {
      flex-direction: column;
    }
  }
}
</style>
